import {useCallback, useEffect, useRef, useState} from "react";
import {Button, Dialog, DialogActions, DialogBody, DialogTitle} from "catalyst";
import {RichMessageText} from "./RichMessageText";
import type {ObjectRef, RichMessage} from "./types";

export type RichErrorDialogProps = {
  message?: RichMessage;
  /** Shown when the server sent no structured message -- the ordinary error text. */
  fallbackText?: string;
  title?: string;
  open?: boolean;
  onClose?: () => void;
  /** Given a token's link, whether this screen can actually reach it. */
  canNavigate?: (ref: ObjectRef) => boolean;
  onNavigate?: (ref: ObjectRef) => void;
};

/**
 * One refusal, said properly.
 *
 * Deliberately not the diagnostics dialog with a list of one. A refusal is the whole outcome of the
 * request rather than a finding within it, so there is no severity to lead with, nothing to keep in
 * the status bar, and no list to scroll -- and a dialog that offers those anyway makes a simple "you
 * can't do that yet" look like a report full of problems.
 *
 * What it does share is the renderer: the same `RichMessageText` that draws diagnostics draws this,
 * so a status reads the same colour here as it does there without anyone coordinating.
 */
export const RichErrorDialog = ({
  message,
  fallbackText,
  title = "Not Allowed",
  open = true,
  onClose,
  canNavigate,
  onNavigate
}: RichErrorDialogProps) => {
  // Own state so the dialog can play its close transition without waiting for the host to hand back
  // a new `open`; `onClose` then tells the host to tear the mount down. Same arrangement as the
  // diagnostics dialog, and for the same reason: GWT mounts it and walks away.
  const [internalOpen, setInternalOpen] = useState(open);

  useEffect(() => {
    setInternalOpen(open);
  }, [open]);

  const contentRef = useRef<HTMLDivElement>(null);
  const closedRef = useRef(false);

  const close = useCallback(() => {
    // Headless UI and the outside-click listener can both fire for one dismissal; the host tears the
    // mount down in response, so it must only be told once.
    if (closedRef.current) {
      return;
    }
    closedRef.current = true;

    setInternalOpen(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!internalOpen) {
      return;
    }

    closedRef.current = false;

    // Headless UI compares against the composed tree it can see, and this dialog is portaled into a
    // shadow-DOM overlay when GWT mounts it -- so a backdrop click looks like it landed on the
    // shadow host, i.e. "inside", and never closes. composedPath() crosses that boundary.
    const onPointerDown = (event: Event) => {
      const panel = contentRef.current?.closest('[role="dialog"]');
      if (!panel) {
        return;
      }

      const path = typeof event.composedPath === "function" ? event.composedPath() : [];
      if (!path.includes(panel)) {
        close();
      }
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [internalOpen, close]);

  const text = fallbackText?.trim();

  return (
    <Dialog open={internalOpen} onClose={close} size="lg">
      <DialogTitle>{title}</DialogTitle>

      <DialogBody>
        <div ref={contentRef} className="text-sm/6 text-brand-900 dark:text-brand-100">
          {message ? (
            <>
              <RichMessageText message={message} canNavigate={canNavigate} onNavigate={onNavigate}/>

              {message.detail ? (
                <div className="mt-2 text-brand-700 dark:text-brand-300">
                  <RichMessageText
                    message={message}
                    expanded
                    canNavigate={canNavigate}
                    onNavigate={onNavigate}/>
                </div>
              ) : null}

              {message.facts?.length ? (
                <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                  {message.facts.map((fact, index) => (
                    <div key={`${fact.label}-${index}`} className="contents">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-300">
                        {fact.label}
                      </dt>
                      <dd>
                        <RichMessageText
                          message={{summary: "{v}", tokens: fact.value ? [{...fact.value, key: "v"}] : []}}/>
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </>
          ) : (
            // No structured message: show what the server did say rather than an empty dialog.
            <pre className="whitespace-pre-wrap font-sans">{text}</pre>
          )}
        </div>
      </DialogBody>

      <DialogActions>
        {/* Catalyst's Button sets cursor-default; an inline style is the one thing that reliably
            wins over it regardless of stylesheet ordering. */}
        <Button style={{cursor: "pointer"}} onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
